import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { PieChart, Loader2, AlertCircle } from 'lucide-react';
import { feeCollectionAPI, securitfeeAPI, formatCurrency } from '../services/api';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale);

const currentDate = new Date();
const currentMonth = currentDate.getMonth() + 1;
const currentYear = currentDate.getFullYear();

const FeeCollectionCharts = ({ appliedMonth: appliedMonthProp, appliedYear: appliedYearProp, searchTrigger: searchTriggerProp = 0 }) => {
  const appliedMonth = appliedMonthProp ?? currentMonth;
  const appliedYear = appliedYearProp ?? currentYear;
  const searchTrigger = searchTriggerProp;

  const [chartData, setChartData] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchChartsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when parent applied period or searchTrigger
  }, [appliedMonth, appliedYear, searchTrigger]);

  const fetchChartsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const periodParams = { month: appliedMonth, year: appliedYear };

      // Prefer /fee-collection/charts?month=&year= (API returns charts + comparison for the period)
      let charts = [];
      let comparison = [];
      try {
        const response = await feeCollectionAPI.getCharts(periodParams);
        const data = response?.data ?? response ?? {};
        const rawCharts = Array.isArray(data.charts) ? data.charts : [];
        const rawComparison = Array.isArray(data.comparison) ? data.comparison : [];
        if (response?.success && (rawCharts.length > 0 || rawComparison.length > 0)) {
          charts = rawCharts;
          comparison = rawComparison;
        }
      } catch (e) {
        console.warn('Charts API error, using overview + by-operator:', e?.message);
      }

      // Fallback: build from overview + by-operator when charts API fails or returns empty
      if (charts.length === 0 && comparison.length === 0) {
        const [overviewRes, byOpRes] = await Promise.all([
          securitfeeAPI.getOverview(periodParams).catch((e) => { console.warn('Overview API error:', e); return null; }),
          securitfeeAPI.getByOperator(periodParams).catch((e) => { console.warn('By-operator API error:', e); return null; })
        ]);

        const rawByOp = Array.isArray(byOpRes) ? byOpRes : (byOpRes?.data ?? []);
        const overview = overviewRes?.data ?? overviewRes ?? {};
        const ov = overview?.overview ?? overview;

        if (rawByOp.length > 0) {
          comparison = rawByOp.map((op) => {
            const expected = Number(op.expectedAmount ?? op.expected ?? 0) || (Number(op.eligibleSubscribers ?? op.eligible ?? 0) * 4000);
            const collected = Number(op.collectedAmount ?? op.collected ?? 0);
            const remaining = Number(op.remainingAmount ?? op.remaining ?? 0);
            const total = expected || (collected + remaining);
            const rate = total ? (collected / total) * 100 : 0;
            const status = rate >= 90 ? 'Excellent' : rate >= 70 ? 'Good' : rate >= 50 ? 'Fair' : 'Poor';
            return {
              serviceType: op.operatorName ?? op.operator ?? op.operator_name ?? 'Unknown',
              target: total,
              collected,
              pending: remaining,
              collectionRate: rate,
              status,
              icon: '📡'
            };
          });
        }

        const totalCollected = Number(ov?.totalCollected ?? overview?.totalCollected ?? 0);
        const remaining = Number(ov?.remaining ?? overview?.remaining ?? 0);
        const totalEstimate = Number(ov?.totalEstimate ?? overview?.totalEstimate ?? 0) || (totalCollected + remaining);
        const overallRate = Number(ov?.collectionRate ?? overview?.collectionRate ?? 0) || (totalEstimate ? (totalCollected / totalEstimate) * 100 : 0);

        if (totalEstimate > 0 || totalCollected > 0 || remaining > 0) {
          charts.push({
            title: 'Overall',
            subtitle: 'All operators',
            icon: '📊',
            data: { collected: totalCollected, pending: remaining, overdue: 0 },
            collectionRate: overallRate,
            collected: totalCollected,
            total: totalEstimate
          });
        }
        rawByOp.forEach((op) => {
          const expected = Number(op.expectedAmount ?? op.expected ?? 0) || (Number(op.eligibleSubscribers ?? op.eligible ?? 0) * 4000);
          const collected = Number(op.collectedAmount ?? op.collected ?? 0);
          const remaining = Number(op.remainingAmount ?? op.remaining ?? 0);
          const total = expected || (collected + remaining);
          const rate = total ? (collected / total) * 100 : 0;
          charts.push({
            title: op.operatorName ?? op.operator ?? op.operator_name ?? 'Operator',
            subtitle: `Eligible: ${op.eligibleSubscribers ?? op.eligible ?? 0}`,
            icon: '📡',
            data: { collected, pending: remaining, overdue: 0 },
            collectionRate: rate,
            collected,
            total
          });
        });
      }

      setChartData(charts);
      setComparisonData(comparison);
    } catch (err) {
      console.error('Error fetching charts data:', err);
      setError('Failed to load chart data. Please try again later.');
      setChartData([]);
      setComparisonData([]);
    } finally {
      setLoading(false);
    }
  };

  const createChartData = (data) => {
    const d = data || {};
    const collected = Number(d.collected ?? d.collectedAmount ?? 0) || 0;
    const pending = Number(d.pending ?? d.pendingAmount ?? 0) || 0;
    const overdue = Number(d.overdue ?? d.overdueAmount ?? 0) || 0;
    return {
      labels: ['Collected', 'Pending', 'Overdue'],
      datasets: [
        {
          data: [collected, pending, overdue],
          backgroundColor: [
            '#10b981', // Green
            '#f59e0b', // Amber
            '#ef4444'  // Red
          ],
          borderWidth: 2,
          borderColor: '#ffffff'
        }
      ]
    };
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
            return `${label}: ${formatCurrency(value)} (${percentage}%)`;
          }
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center mb-6">
          <PieChart className="h-6 w-6 text-primary-600 mr-3" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">Fee Collection Analysis</h2>
            <p className="text-gray-600">Visual breakdown of fee collections by service type</p>
          </div>
        </div>
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mr-3" />
          <span className="text-gray-600">Loading chart data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center mb-6">
          <PieChart className="h-6 w-6 text-primary-600 mr-3" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">Fee Collection Analysis</h2>
            <p className="text-gray-600">Visual breakdown of fee collections by service type</p>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-red-800">Error Loading Data</h3>
              <p className="text-red-600 mt-1">{error}</p>
              <button
                onClick={fetchChartsData}
                className="mt-3 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="space-y-6">
      <div className="flex items-center mb-6">
        <PieChart className="h-6 w-6 text-primary-600 mr-3" />
        <div>
          <h2 className="text-xl font-bold text-gray-900">Fee Collection Analysis</h2>
          <p className="text-gray-600">Visual breakdown of fee collections by service type</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {chartData.map((chart, index) => {
          const dataSource = chart?.data ?? chart;
          const chartDataFormatted = createChartData(dataSource);
          const title = chart?.title ?? chart?.name ?? `Chart ${index + 1}`;
          const subtitle = chart?.subtitle ?? '';
          const icon = chart?.icon ?? '📊';
          const collectionRate = Number(chart?.collectionRate ?? chart?.collection_rate ?? 0) || 0;
          const collected = chart?.collected ?? dataSource?.collected ?? dataSource?.collectedAmount ?? 0;
          const total = chart?.total ?? chart?.target ?? dataSource?.total ?? dataSource?.target ?? 0;

          return (
            <div
              key={chart?.id ?? chart?.title ?? index}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-200"
            >
              {/* Chart Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 flex items-center">
                    <span className="mr-2 text-xl">{icon}</span>
                    {title}
                  </h3>
                  {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">
                    {collectionRate.toFixed(1)}%
                  </div>
                  <div className="text-xs text-gray-500">Collection Rate</div>
                </div>
              </div>

              {/* Chart */}
              <div className="h-64 mb-4">
                <Pie data={chartDataFormatted} options={chartOptions} />
              </div>

              {/* Summary Stats */}
              <div className="border-t pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-600">
                      {formatCurrency(collected)}
                    </div>
                    <div className="text-xs text-gray-600">Collected</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-gray-900">
                      {formatCurrency(total)}
                    </div>
                    <div className="text-xs text-gray-600">Total Target</div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>Progress</span>
                    <span>{collectionRate.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(collectionRate, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Comparison Table */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Collection Comparison</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Collected
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pending
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {comparisonData.map((row, index) => {
                const getStatusStyle = (status) => {
                  switch (String(status || '')) {
                    case 'Excellent': return 'bg-green-100 text-green-800';
                    case 'Good': return 'bg-blue-100 text-blue-800';
                    case 'Fair': return 'bg-yellow-100 text-yellow-800';
                    case 'Poor': return 'bg-red-100 text-red-800';
                    default: return 'bg-gray-100 text-gray-800';
                  }
                };
                const serviceType = row.serviceType ?? row.service_type ?? row.name ?? `Row ${index + 1}`;
                const getBgColor = (st) => (String(st).toUpperCase() === 'MBB' ? 'bg-blue-50' : 'bg-green-50');
                const getTextColor = (st) => (String(st).toUpperCase() === 'MBB' ? 'text-blue-600' : 'text-green-600');
                const target = row.target ?? row.total ?? row.expected ?? 0;
                const collected = row.collected ?? row.collectedAmount ?? 0;
                const pending = row.pending ?? row.pendingAmount ?? row.remaining ?? 0;
                const rate = Number(row.collectionRate ?? row.collection_rate ?? row.rate ?? 0) || 0;
                const status = row.status ?? (rate >= 90 ? 'Excellent' : rate >= 70 ? 'Good' : rate >= 50 ? 'Fair' : 'Poor');

                return (
                  <tr key={row.id ?? row.serviceType ?? index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`mr-3 w-8 h-8 rounded-lg ${getBgColor(serviceType)} flex items-center justify-center`}>
                          <span className={`${getTextColor(serviceType)} text-xs font-bold`}>{row.icon ?? (String(serviceType).toUpperCase() === 'MBB' ? 'M' : 'F')}</span>
                        </div>
                        <div className="text-sm font-medium text-gray-900">{serviceType}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(target)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                      {formatCurrency(collected)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600">
                      {formatCurrency(pending)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {rate.toFixed(1)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusStyle(status)}`}>
                        {status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FeeCollectionCharts;